import { SxProps, Theme } from '@mui/material';

export const dialogSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  '& .MuiDialog-paper': {
    borderRadius: backrs.radius['radius-xl'],
    boxShadow: backrs.shadows['shadow-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    padding: 0,
    margin: 0,
    maxWidth: 'none',
    width: '640px',
    height: 'max(480px, 40vh)',
    minHeight: '300px',
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    [breakpoints.down('md')]: {
      width: 'calc(100vw - 32px)',
      height: 'max(400px, 40vh)',
      margin: backrs.spacing['spacing-md'],
    },
    [breakpoints.down('sm')]: {
      width: 'calc(100vw - 16px)',
      height: 'max(300px, 40vh)',
      margin: backrs.spacing['spacing-sm'],
    },
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
});

export const containerSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  padding: backrs.spacing['spacing-lg'],
  [breakpoints.down('md')]: {
    padding: backrs.spacing['spacing-md'],
  },
  [breakpoints.down('sm')]: {
    padding: backrs.spacing['spacing-sm'],
  },
});

export const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  marginBottom: backrs.spacing['spacing-md'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  paddingBottom: backrs.spacing['spacing-md'],
});

export const headerRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
});

export const titleSx: SxProps<Theme> = {
  flexShrink: 0,
};

export const searchContainerSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  flex: 1,
  padding: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-md']}`,
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  [breakpoints.down('sm')]: {
    padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-sm']}`,
    minHeight: '44px', // Ensure adequate touch target size
  },
});

export const searchInputSx: SxProps<Theme> = {
  '& .MuiInputBase-input': {
    padding: 0,
    fontSize: '14px',
  },
};

export const closeButtonSx: SxProps<Theme> = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '4px',
  },
};

export const contentSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  overflowY: 'auto',
  marginBottom: backrs.spacing['spacing-md'],
  minHeight: 0,
});

export const createItemContainerSx =
  (hasFocus: boolean, isSelected: boolean): SxProps<Theme> =>
  ({ backrs }) => {
    let backgroundColor = 'transparent';

    if (isSelected) {
      // Actual selection - darkest/most prominent
      backgroundColor = backrs.colors['Colors/Background/bg-quaternary'];
    } else if (hasFocus) {
      // Keyboard focus - lighter than selection
      backgroundColor = backrs.colors['Colors/Background/bg-secondary'];
    }

    return {
      padding: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-md']}`,
      cursor: 'pointer',
      borderRadius: backrs.radius['radius-md'],
      margin: `${backrs.spacing['spacing-xs']} 0`,
      transition: 'all 0.2s ease-in-out',
      backgroundColor,
      '&:hover': {
        backgroundColor: isSelected
          ? backrs.colors['Colors/Background/bg-quaternary'] // Keep selection color on hover
          : backrs.colors['Colors/Background/bg-tertiary'], // Hover state - medium contrast
      },
    };
  };

export const sectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'block',
  padding: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-md']}`,
  fontWeight: 600,
  color: backrs.colors['Colors/Foreground/fg-secondary'],
  fontSize: '12px',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
});

export const footerSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  paddingTop: backrs.spacing['spacing-md'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

export const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-sm'],
  justifyContent: 'flex-end',
});

export const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-xl'],
});

export const emptyStateContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-xl'],
});
